import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useToast } from '@/hooks/use-toast';
import { DealItemWithVersions } from '@/lib/graphql/generated/graphql';
import { isDealNotEditableError } from '@/lib/utils/deal-error';
import { pendingOptimisticUpdatesState } from '@/states/pending-optimistic-updates.state';
import { productsMapState } from '@/states/products.state';
import {
  type ProductModification,
  recentlyModifiedProductsState,
  updateRecentlyModifiedProducts,
} from '@/states/recently-modified-products.state';
import { selectedProductIdsState } from '@/states/row-selection.state';
import { useAtomValue, useSetAtom, useStore } from 'jotai';

import { useDealNotEditableHandler } from './useDealNotEditableHandler';

export interface ExecuteOptimisticUpdateOptions<T> {
  optimisticUpdate: (prev: DealItemWithVersions[]) => DealItemWithVersions[];
  mutation: () => Promise<T>;
  checkSuccess: (result: T) => boolean;
  getErrorMessage: (result: T) => string;
  getCaughtErrorMessage?: (error: unknown) => string;
  defaultErrorMessage: string;
  errorContext: string;
  onSuccess?: () => void;
  onError?: (errorMessage: string) => void;
  onSuccessClose?: () => void;
  itemIds?: string[];
  inProgressMessage?: string;
  /** Fields that were modified (for highlighting) */
  modifiedFields?: string[];
}

/**
 * Hook that provides a reusable executeOptimisticUpdate function.
 * This centralizes the optimistic update logic used across multiple components.
 */
export const useExecuteOptimisticUpdate = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const store = useStore();
  const setProductsMap = useSetAtom(productsMapState);
  const selectedProductIds = useAtomValue(selectedProductIdsState);
  const setPendingOptimisticUpdates = useSetAtom(pendingOptimisticUpdatesState);
  const setRecentlyModified = useSetAtom(recentlyModifiedProductsState);
  const { handleIfDealNotEditable } = useDealNotEditableHandler();

  const rollbackItems = useCallback(
    (previousMap: Map<string, DealItemWithVersions>, itemIds: string[]) => {
      setProductsMap((currentMap) => {
        const next = new Map(currentMap);
        for (const id of itemIds) {
          const prev = previousMap.get(id);
          if (prev) next.set(id, prev);
          else next.delete(id);
        }
        return next;
      });
    },
    [setProductsMap]
  );

  const executeOptimisticUpdate = useCallback(
    async <T>(options: ExecuteOptimisticUpdateOptions<T>): Promise<boolean> => {
      const previousMap = store.get(productsMapState);
      const itemIds = options.itemIds || selectedProductIds;

      setPendingOptimisticUpdates(
        (prev: Set<string>) => new Set([...prev, ...itemIds])
      );
      setProductsMap((prevMap) => {
        const nextArray = options.optimisticUpdate(
          Array.from(prevMap.values())
        );
        const nextMap = new Map<string, DealItemWithVersions>();
        for (const p of nextArray) nextMap.set(p.id, p);
        return nextMap;
      });

      let inProgressToast: ReturnType<typeof toast> | undefined;
      try {
        if (options.inProgressMessage) {
          inProgressToast = toast({
            description: `⏳ ${options.inProgressMessage}`,
          });
        }
        const result = await options.mutation();

        if (options.checkSuccess(result)) {
          // Dismiss the in-progress toast if it exists
          if (inProgressToast) {
            inProgressToast.dismiss();
          }
          // Clear pending state so subscription updates can apply server values (e.g. productGroupKey)
          setPendingOptimisticUpdates((prev: Set<string>) => {
            const next = new Set(prev);
            itemIds.forEach((id) => next.delete(id));
            return next;
          });
          // Mark products and fields as recently modified for highlighting
          // Also clean up expired entries to prevent memory leaks
          const now = Date.now();
          const fieldsToMark = options.modifiedFields || [];
          const modifications: ProductModification[] = itemIds.map((id) => ({
            productId: id,
            fields: fieldsToMark,
          }));

          setRecentlyModified((prev) =>
            updateRecentlyModifiedProducts(prev, now, modifications)
          );
          options.onSuccess?.();
          if (options.onSuccessClose) {
            options.onSuccessClose();
          }
          return true;
        } else {
          // Dismiss the in-progress toast if it exists
          if (inProgressToast) {
            inProgressToast.dismiss();
          }
          setPendingOptimisticUpdates((prev: Set<string>) => {
            const next = new Set(prev);
            itemIds.forEach((id) => next.delete(id));
            return next;
          });
          rollbackItems(previousMap, itemIds);

          const errorMessage = options.getErrorMessage(result);

          // Handle dealNotEditable specifically: show translated toast + refetch deal
          if (isDealNotEditableError(errorMessage)) {
            handleIfDealNotEditable(errorMessage);
            options.onError?.(errorMessage);
            return false;
          }

          toast({
            title: t('general.error'),
            description: errorMessage,
            variant: 'destructive',
          });
          options.onError?.(errorMessage);
          return false;
        }
      } catch (err) {
        // Dismiss the in-progress toast if it exists
        if (inProgressToast) {
          inProgressToast.dismiss();
        }
        setPendingOptimisticUpdates((prev: Set<string>) => {
          const next = new Set(prev);
          itemIds.forEach((id) => next.delete(id));
          return next;
        });
        setProductsMap(previousMap);
        console.error(`[${options.errorContext}] Error:`, err);
        const errorMessage =
          options.getCaughtErrorMessage?.(err) ?? options.defaultErrorMessage;
        toast({
          title: t('general.error'),
          description: errorMessage,
          variant: 'destructive',
        });
        options.onError?.(errorMessage);
        return false;
      }
    },
    [
      store,
      rollbackItems,
      selectedProductIds,
      setProductsMap,
      setPendingOptimisticUpdates,
      setRecentlyModified,
      toast,
      t,
      handleIfDealNotEditable,
    ]
  );

  return { executeOptimisticUpdate };
};
